import { type ReactNode, useState, useEffect } from 'react';
import { authenticatedFetch } from '../../../api/authenticatedFetch';
import { useToolScope } from '../../../features/toolExperience/useToolScope';
import { scopeKey } from '../../../features/toolExperience/types';
import { useToast } from '../../../hooks/useToast';
import {
  useClusteringClusters,
  useRenameCluster,
} from '../../../hooks/useClustering';
import type {
  ClustersListParams,
  FunnelStage,
  SearchIntent,
} from '../../../types/clustering';
import { HelpTooltip } from '../../Tooltip/Tooltip';
import { ClusterRow, SIGNAL_LEGEND } from './ClusterRow';
import styles from './Results.module.css';

interface ClustersTableProps {
  batchId: string;
  canEdit: boolean;
}

type SortKey = NonNullable<ClustersListParams['sort']>;

interface HeaderColumn {
  label: string;
  help: ReactNode;
  ariaLabel: string;
}

interface SortHeaderColumn extends HeaderColumn {
  key: SortKey;
}

const SORT_COLUMNS: SortHeaderColumn[] = [
  {
    key: 'keywords_count',
    label: '# KWs',
    help: 'How many keywords are grouped into this cluster.',
    ariaLabel: 'Explain keywords count column',
  },
  {
    key: 'search_volume',
    label: 'Volume',
    help: 'Estimated total monthly search volume across the keywords in this cluster.',
    ariaLabel: 'Explain volume column',
  },
  {
    key: 'opportunity_score',
    label: 'Opportunity',
    help: 'Combined priority score based on search volume, ranking opportunity, competition, and cluster size. Higher is better.',
    ariaLabel: 'Explain opportunity column',
  },
];

const STATIC_COLUMNS: HeaderColumn[] = [
  {
    label: 'Cluster',
    help: 'The primary label for the cluster. Expand a row to see every keyword grouped under it.',
    ariaLabel: 'Explain cluster column',
  },
  {
    label: 'Intent',
    help: 'The dominant search intent inferred from the keywords and SERP results in this cluster.',
    ariaLabel: 'Explain intent column',
  },
  {
    label: 'Funnel',
    help: 'The dominant funnel stage for the cluster, from awareness to purchase intent.',
    ariaLabel: 'Explain funnel column',
  },
  {
    label: 'Source',
    help: 'How the cluster label was chosen, such as the hub keyword or a merged result.',
    ariaLabel: 'Explain source column',
  },
];

export function ClustersTable({ batchId, canEdit }: ClustersTableProps) {
  const workspace = scopeKey(useToolScope());
  const [contextEnabled, setContextEnabled] = useState(false);
  useEffect(() => {
    let current = true;
    setContextEnabled(false);
    authenticatedFetch<{ site_context_enabled?: boolean }>('/api/v1/briefing/config/brief-v2')
      .then(config => { if (current) setContextEnabled(!!config.site_context_enabled); }).catch(() => {});
    return () => { current = false; };
  }, [workspace]);
  const toast = useToast();
  const [filters, setFilters] = useState<ClustersListParams>({
    sort: 'opportunity_score',
    order: 'desc',
    include_merged: false,
    limit: 200,
    offset: 0,
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: clusters = [], isLoading, error } = useClusteringClusters(batchId, filters);
  const renameMut = useRenameCluster(batchId);

  const toggleSort = (key: SortKey) => {
    setFilters((prev) => ({
      ...prev,
      sort: key,
      order: prev.sort === key && prev.order === 'desc' ? 'asc' : 'desc',
      offset: 0,
    }));
  };

  const sortIndicator = (key: SortKey) => {
    if (filters.sort !== key) return null;
    return <span className={styles.sortArrow}>{filters.order === 'desc' ? '▼' : '▲'}</span>;
  };

  const signalLegend = (
    <ul className={styles.signalLegendList}>
      {SIGNAL_LEGEND.map((signal) => (
        <li key={signal.key}>
          <strong>{signal.label}</strong> = {signal.name}
        </li>
      ))}
    </ul>
  );

  const renderHelpButton = (content: ReactNode, ariaLabel: string) => (
    <HelpTooltip content={content} placement="bottom">
      <button
        type="button"
        className={styles.headerHelpButton}
        aria-label={ariaLabel}
        onClick={(event) => event.stopPropagation()}
      >
        ?
      </button>
    </HelpTooltip>
  );

  const renderHeader = (
    label: string,
    help: ReactNode,
    ariaLabel: string,
    extra?: ReactNode,
  ) => (
    <div className={styles.headerWithHelp}>
      <span>
        {label}
        {extra}
      </span>
      {renderHelpButton(help, ariaLabel)}
    </div>
  );

  return (
    <div>
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="flt-intent">
            Intent
          </label>
          <select
            id="flt-intent"
            className={styles.filterControl}
            value={filters.search_intent ?? ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                search_intent: (e.target.value || undefined) as SearchIntent | undefined,
                offset: 0,
              })
            }
          >
            <option value="">Any</option>
            <option value="informational">Informational</option>
            <option value="navigational">Navigational</option>
            <option value="commercial investigation">Commercial investigation</option>
            <option value="transactional">Transactional</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="flt-funnel">
            Funnel stage
          </label>
          <select
            id="flt-funnel"
            className={styles.filterControl}
            value={filters.funnel_stage ?? ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                funnel_stage: (e.target.value || undefined) as FunnelStage | undefined,
                offset: 0,
              })
            }
          >
            <option value="">Any</option>
            <option value="Top of Funnel">Top of Funnel</option>
            <option value="Middle of Funnel">Middle of Funnel</option>
            <option value="Bottom of Funnel">Bottom of Funnel</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="flt-minvol">
            Min volume
          </label>
          <input
            id="flt-minvol"
            className={styles.filterControl}
            type="number"
            min={0}
            value={filters.min_volume ?? ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                min_volume: e.target.value ? Number(e.target.value) : undefined,
                offset: 0,
              })
            }
          />
        </div>
        <label className={styles.filterCheckbox}>
          <input
            type="checkbox"
            checked={!!filters.include_merged}
            onChange={(e) =>
              setFilters({ ...filters, include_merged: e.target.checked, offset: 0 })
            }
          />
          Include merged
        </label>
      </div>

      {error && <div className={styles.empty}>{(error as Error).message}</div>}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                {renderHeader(
                  STATIC_COLUMNS[0].label,
                  STATIC_COLUMNS[0].help,
                  STATIC_COLUMNS[0].ariaLabel,
                )}
              </th>
              {SORT_COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={styles['th--sortable']}
                  onClick={() => toggleSort(c.key)}
                >
                  {renderHeader(c.label, c.help, c.ariaLabel, sortIndicator(c.key))}
                </th>
              ))}
              <th>
                {renderHeader(
                  STATIC_COLUMNS[1].label,
                  STATIC_COLUMNS[1].help,
                  STATIC_COLUMNS[1].ariaLabel,
                )}
              </th>
              <th>
                {renderHeader(
                  STATIC_COLUMNS[2].label,
                  STATIC_COLUMNS[2].help,
                  STATIC_COLUMNS[2].ariaLabel,
                )}
              </th>
              <th>
                {renderHeader(
                  STATIC_COLUMNS[3].label,
                  STATIC_COLUMNS[3].help,
                  STATIC_COLUMNS[3].ariaLabel,
                )}
              </th>
              <th>
                {renderHeader('Signals', signalLegend, 'Explain signal abbreviations')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className={styles.empty}>Loading clusters…</td>
              </tr>
            ) : clusters.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.empty}>No clusters match these filters.</td>
              </tr>
            ) : (
              clusters.map((cluster) => (
                <ClusterRow
                  canUseInBrief={contextEnabled}
                  key={cluster.id}
                  cluster={cluster}
                  canEdit={canEdit}
                  isExpanded={expanded === cluster.id}
                  isRenaming={renameMut.isPending}
                  onToggle={() => setExpanded(expanded === cluster.id ? null : cluster.id)}
                  onRename={async (next) => {
                    try {
                      await renameMut.mutateAsync({
                        clusterId: cluster.id,
                        body: { cluster_label: next },
                      });
                      toast.success('Cluster renamed');
                    } catch (err) {
                      toast.error('Rename failed', (err as Error).message);
                    }
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
